/**
 * useDocumentDirection
 *
 * Mirrors the currently selected language from the bound store onto the
 * `<html>` element as `dir` and `lang` attributes. The writing direction
 * is sourced from the backend-provided `supportedLanguagesList` so any
 * new RTL locale (he, ur, fa, ...) lights up with zero FE changes.
 *
 * Intended to be mounted exactly once at the root (see App.tsx).
 */

import { useEffect } from 'react';

import { useBoundStore } from '@/stores';
import type { LanguageDirection } from '@/stores/slices/languageSlice';

const DEFAULT_DIRECTION: LanguageDirection = 'ltr';

/**
 * Returns the writing direction for the currently selected language. Components
 * can use this to pass `dir` explicitly to Radix primitives (e.g. ScrollArea)
 * that stamp `dir="ltr"` on their own wrapper and would otherwise short-circuit
 * the inherited `<html dir="rtl">`.
 */
export const useLanguageDirection = (): LanguageDirection => {
  const currentLanguage = useBoundStore.use.currentLanguage();
  const supportedLanguagesList = useBoundStore.use.supportedLanguagesList();

  const entry = supportedLanguagesList?.find((l) => l.code === currentLanguage);
  return entry?.dir ?? DEFAULT_DIRECTION;
};

export const useDocumentDirection = (): void => {
  const currentLanguage = useBoundStore.use.currentLanguage();
  const dir = useLanguageDirection();

  useEffect(() => {
    if (typeof document === 'undefined') return;

    const html = document.documentElement;
    if (html.getAttribute('dir') !== dir) html.setAttribute('dir', dir);
    if (html.getAttribute('lang') !== currentLanguage) html.setAttribute('lang', currentLanguage);
  }, [currentLanguage, dir]);
};

/** Headless component: mount once near the app root to sync `<html dir/lang>`. */
export const DocumentDirectionSync = (): null => {
  useDocumentDirection();
  return null;
};
